import React from 'react';
import {
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TextField,
  Checkbox,
  Typography
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useTranslations } from 'next-intl';

interface AddSubTaskProps {
  isAddingSubTask: boolean;
  setIsAddingSubTask: (isAdding: boolean) => void;
  newSubTaskContent: string;
  setNewSubTaskContent: (content: string) => void;
  handleAddSubTask: () => void;
  handleBlur: () => void;
}

export const AddSubTask: React.FC<AddSubTaskProps> = ({
  isAddingSubTask,
  setIsAddingSubTask,
  newSubTaskContent,
  setNewSubTaskContent,
  handleAddSubTask,
  handleBlur
}) => {
  const t = useTranslations("TaskManager");

  if (isAddingSubTask) {
    return (
      <ListItem
        dense={true}
        sx={{
          borderRadius: 16,
          backgroundColor: 'var(--color-muted)',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: 'var(--color-primary-light)',
          },
          minHeight: '16px',
          mt: 1,
          mb: 1
        }}
      >
        <ListItemIcon sx={{ minWidth: 36 }}>
          <Checkbox
            checked={false}
            icon={<RadioButtonUncheckedIcon />}
            checkedIcon={<CheckCircleIcon />}
            sx={{
              color: 'var(--color-primary)',
              '&.Mui-checked': {
                color: 'var(--color-success)'
              }
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={
            <TextField
              fullWidth
              variant="standard"
              placeholder={t('SubTasks.addNewSubTaskContent')}
              value={newSubTaskContent}
              onChange={(e) => setNewSubTaskContent(e.target.value)}
              onBlur={handleBlur}
              size="small"
              sx={{
                '& .MuiInput-underline:before': { borderBottom: 'none' },
                '& .MuiInput-underline:after': { borderBottom: 'none' },
                '& .MuiInput-underline:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                '& .MuiInputBase-input': {
                  padding: 0,
                  fontWeight: 500,
                  color: 'var(--color-foreground)',
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleAddSubTask();
                }
              }}
              autoFocus
            />
          }
        />
        <IconButton
          edge="end"
          size="small"
          onClick={() => {
            setIsAddingSubTask(false);
            setNewSubTaskContent('');
          }}
          sx={{
            color: 'var(--color-button-delete)',
            opacity: 0.7,
            '&:hover': {
              opacity: 1,
              backgroundColor: 'var(--color-button-delete)' + '15'
            }
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </ListItem>
    );
  }

  return (
    <ListItem
      dense
      onClick={() => setIsAddingSubTask(true)}
      sx={{
        borderRadius: 16,
        mb: 1,
        backgroundColor: 'var(--color-muted)',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'var(--color-primary-light)',
        },
        minHeight: '20px',
        mt: 1
      }}
    >
      <ListItemIcon sx={{ minWidth: 36 }}>
        <Checkbox
          checked={false}
          icon={<RadioButtonUncheckedIcon />}
          checkedIcon={<CheckCircleIcon />}
          sx={{
            color: 'var(--color-primary)',
            '&.Mui-checked': {
              color: 'var(--color-success)'
            }
          }}
        />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography
            sx={{
              color: 'var(--color-muted-foreground)',
              fontWeight: 400,
              fontStyle: 'italic'
            }}
          >
            {t('SubTasks.addNewSubTask')}
          </Typography>
        }
      />
    </ListItem>
  );
};